import { useState, useEffect } from 'react';
import { getApp } from 'firebase/app';
import {
  getFirestore, collection, doc, onSnapshot, updateDoc,
  query, where, arrayUnion,
} from 'firebase/firestore';

const DB = () => { try { return getFirestore(getApp()); } catch (_) { return null; } };

const PRIORITY_COLOR = { info: '#65d4fa', warning: '#fbbf24', critical: '#f87171' };
const PRIORITY_ICON  = { info: 'ℹ️', warning: '⚠️', critical: '🚨' };
const PRIORITY_ORDER = { critical: 0, warning: 1, info: 2 };

export default function AnnouncementBanner({ userId, userRole }) {
  const [banners, setBanners] = useState([]);

  useEffect(() => {
    const db = DB();
    if (!db || !userId) return;
    const now = new Date();

    const unsub = onSnapshot(
      query(collection(db, 'announcements'), where('isActive', '==', true)),
      snap => {
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const visible = all.filter(a => {
          // Check publish time
          if (a.publishAt?.toDate && a.publishAt.toDate() > now) return false;
          // Check expiry
          if (a.expiresAt?.toDate && a.expiresAt.toDate() < now) return false;
          // Check target roles (empty = all)
          if (a.targetRoles?.length > 0 && !a.targetRoles.includes(userRole)) return false;
          // Check if user dismissed (critical: always show)
          if (a.priority !== 'critical' && (a.readBy || []).includes(userId)) return false;
          return true;
        });
        visible.sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 3) - (PRIORITY_ORDER[b.priority] ?? 3));
        setBanners(visible);
      },
      () => {}
    );
    return unsub;
  }, [userId, userRole]);

  async function dismiss(id) {
    const db = DB();
    if (!db) return;
    try {
      await updateDoc(doc(db, 'announcements', id), { readBy: arrayUnion(userId) });
    } catch (_) {}
    setBanners(b => b.filter(a => a.id !== id));
  }

  if (!banners.length) return null;

  return (
    <div style={{ position: 'sticky', top: 'var(--smc-nav-h, 46px)', zIndex: 92 }}>
      {banners.map(a => {
        const pc = PRIORITY_COLOR[a.priority] || '#65d4fa';
        const isCritical = a.priority === 'critical';
        return (
          <div key={a.id}
            style={{
              background: `linear-gradient(90deg, ${pc}28 0%, ${pc}10 50%, ${pc}06 100%)`,
              borderBottom: `1px solid ${pc}40`,
              borderLeft: `4px solid ${pc}`,
              borderTop: isCritical ? `1px solid ${pc}40` : 'none',
              padding: '11px 16px', display: 'flex', alignItems: 'flex-start', gap: 10,
              boxShadow: `inset 0 0 0 1px ${pc}12`,
            }}>
            <span style={{ fontSize: 15, flexShrink: 0, marginTop: 1 }}>{PRIORITY_ICON[a.priority] || 'ℹ️'}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontFamily: "'Inter',system-ui,sans-serif", fontSize: 13, fontWeight: 700,
                color: pc, textShadow: `0 0 12px ${pc}44` }}>
                {a.title}
              </span>
              {a.body && (
                <span style={{ fontFamily: "'Inter',system-ui,sans-serif", fontSize: 12,
                  color: 'var(--smc-tx, #d9e2ff)', marginLeft: 8, opacity: 0.85 }}>
                  {a.body}
                </span>
              )}
            </div>
            {!isCritical && (
              <button onClick={() => dismiss(a.id)}
                style={{ background: 'none', border: 'none', color: 'var(--smc-tx3, #64748b)',
                  cursor: 'pointer', fontSize: 16, lineHeight: 1, flexShrink: 0, padding: '0 4px' }}>
                ×
              </button>
            )}
            {isCritical && (
              <span style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: 8, color: pc,
                letterSpacing: 1, flexShrink: 0, alignSelf: 'center' }}>CRITICAL</span>
            )}
          </div>
        );
      })}
    </div>
  );
}
